import React, { useEffect, useState, useCallback } from "react";
import smsNotificationService from "../../services/smsNotificationService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import styles from "./SmsNotification.module.css";

const SmsNotification = () => {
  const [activeTab, setActiveTab] = useState("send");

  // ── Dashboard stats ──────────────────────────────
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await smsNotificationService.getDashboardStats();
      setStats(res?.data || null);
    } catch (err) {
      console.error("Failed to load dashboard stats:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // ── Send OTP ─────────────────────────────────────
  const [sendIdentifier, setSendIdentifier] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!sendIdentifier.trim()) {
      toast.error("Please enter a mobile number");
      return;
    }
    setSendLoading(true);
    setSendResult(null);
    try {
      const res = await smsNotificationService.sendOTP({ identifier: sendIdentifier.trim() });
      setSendResult({ type: "success", message: res?.message || "OTP sent successfully", data: res?.data });
      toast.success("OTP sent successfully");
      fetchStats();
    } catch (err) {
      setSendResult({ type: "error", message: err?.message || "Failed to send OTP" });
      toast.error(err?.message || "Failed to send OTP");
    } finally {
      setSendLoading(false);
    }
  };

  // ── Verify OTP ───────────────────────────────────
  const [verifyIdentifier, setVerifyIdentifier] = useState("");
  const [verifyOtp, setVerifyOtp] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!verifyIdentifier.trim() || !verifyOtp.trim()) {
      toast.error("Please enter mobile number and OTP");
      return;
    }
    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const res = await smsNotificationService.verifyOTP({ identifier: verifyIdentifier.trim(), otp: verifyOtp.trim() });
      setVerifyResult({ type: "success", message: res?.message || "OTP verified successfully" });
      toast.success("OTP verified successfully");
      setVerifyOtp("");
      fetchStats();
    } catch (err) {
      setVerifyResult({ type: "error", message: err?.message || "OTP verification failed" });
      toast.error(err?.message || "OTP verification failed");
    } finally {
      setVerifyLoading(false);
    }
  };

  // ── Logs ─────────────────────────────────────────
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ totalRecords: 0, totalPages: 1, currentPage: 1, limit: 20 });
  const [logSearch, setLogSearch] = useState("");
  const [logStatusFilter, setLogStatusFilter] = useState("");

  const fetchLogs = useCallback(async (page = 1) => {
    setLogsLoading(true);
    try {
      const params = { page, limit: 20 };
      if (logSearch.trim()) params.identifier = logSearch.trim();
      if (logStatusFilter) params.status = logStatusFilter;
      const res = await smsNotificationService.getOtpLogs(params);
      setLogs(res?.data || []);
      setLogsPagination(res?.pagination || { totalRecords: 0, totalPages: 1, currentPage: page, limit: 20 });
    } catch (err) {
      console.error("Failed to fetch logs:", err);
      toast.error("Failed to fetch logs");
    } finally {
      setLogsLoading(false);
    }
  }, [logSearch, logStatusFilter]);

  useEffect(() => {
    if (activeTab === "logs") {
      fetchLogs(1);
    }
  }, [activeTab, fetchLogs]);

  // ── Helpers ──────────────────────────────────────
  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  };

  const renderPagination = () => {
    const { totalPages, currentPage, totalRecords, limit } = logsPagination;
    if (totalPages <= 1) return null;

    const pages = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return (
      <div className={styles.pagination}>
        <span className={styles.paginationInfo}>
          Showing {((currentPage - 1) * limit) + 1}–{Math.min(currentPage * limit, totalRecords)} of {totalRecords}
        </span>
        <div className={styles.paginationControls}>
          <button className={styles.pageBtn} disabled={currentPage === 1} onClick={() => fetchLogs(currentPage - 1)}>
            ‹
          </button>
          {pages.map((p) => (
            <button key={p} className={`${styles.pageBtn} ${p === currentPage ? styles.active : ""}`} onClick={() => fetchLogs(p)}>
              {p}
            </button>
          ))}
          <button className={styles.pageBtn} disabled={currentPage === totalPages} onClick={() => fetchLogs(currentPage + 1)}>
            ›
          </button>
        </div>
      </div>
    );
  };

  // ── Render ───────────────────────────────────────
  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>SMS Notification</h1>
          <p className={styles.pageSubtitle}>Send and verify OTP via MSG91 Widget API</p>
        </div>
      </div>

      {/* Dashboard Stats */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Total Sent</span>
          <span className={`${styles.statValue} ${styles.purple}`}>{statsLoading ? "…" : stats?.totalSent ?? 0}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Verified</span>
          <span className={`${styles.statValue} ${styles.success}`}>{statsLoading ? "…" : stats?.verified ?? 0}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Failed</span>
          <span className={`${styles.statValue} ${styles.danger}`}>{statsLoading ? "…" : stats?.failed ?? 0}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Success Rate</span>
          <span className={`${styles.statValue} ${styles.accent}`}>{statsLoading ? "…" : `${stats?.successRate ?? 0}%`}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Today's Requests</span>
          <span className={styles.statValue}>{statsLoading ? "…" : stats?.todayRequests ?? 0}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button className={`${styles.tab} ${activeTab === "send" ? styles.active : ""}`} onClick={() => setActiveTab("send")}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>send</span>
          Send OTP
        </button>
        <button className={`${styles.tab} ${activeTab === "verify" ? styles.active : ""}`} onClick={() => setActiveTab("verify")}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>verified</span>
          Verify OTP
        </button>
        <button className={`${styles.tab} ${activeTab === "logs" ? styles.active : ""}`} onClick={() => setActiveTab("logs")}>
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>list_alt</span>
          Logs
        </button>
      </div>

      {/* ── Tab: Send OTP ──────────────────────────── */}
      {activeTab === "send" && (
        <div className={styles.formPanel}>
          <h2 className={styles.formTitle}>Send OTP</h2>
          <p className={styles.formSubtitle}>Enter a mobile number to send a one-time password via SMS</p>
          <form onSubmit={handleSendOTP}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Mobile Number</label>
              <input
                className={styles.fieldInput}
                type="text"
                placeholder="e.g. 919876543210"
                value={sendIdentifier}
                onChange={(e) => setSendIdentifier(e.target.value)}
                disabled={sendLoading}
              />
            </div>
            <button className={styles.submitBtn} type="submit" disabled={sendLoading}>
              {sendLoading ? "Sending…" : "Send OTP"}
            </button>
          </form>
          {sendResult && (
            <div className={`${styles.resultBox} ${sendResult.type === "success" ? styles.success : styles.error}`}>
              <strong>{sendResult.type === "success" ? "✓ Success" : "✕ Error"}:</strong> {sendResult.message}
              {sendResult.data?.identifier && (
                <div style={{ marginTop: 8 }}>Sent to: <strong>{sendResult.data.identifier}</strong></div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Verify OTP ────────────────────────── */}
      {activeTab === "verify" && (
        <div className={styles.formPanel}>
          <h2 className={styles.formTitle}>Verify OTP</h2>
          <p className={styles.formSubtitle}>Enter the mobile number and the OTP received to verify</p>
          <form onSubmit={handleVerifyOTP}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Mobile Number</label>
              <input
                className={styles.fieldInput}
                type="text"
                placeholder="e.g. 919876543210"
                value={verifyIdentifier}
                onChange={(e) => setVerifyIdentifier(e.target.value)}
                disabled={verifyLoading}
              />
            </div>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>OTP</label>
              <input
                className={styles.fieldInput}
                type="text"
                placeholder="Enter OTP"
                value={verifyOtp}
                onChange={(e) => setVerifyOtp(e.target.value)}
                maxLength={6}
                disabled={verifyLoading}
              />
            </div>
            <button className={styles.submitBtn} type="submit" disabled={verifyLoading}>
              {verifyLoading ? "Verifying…" : "Verify OTP"}
            </button>
          </form>
          {verifyResult && (
            <div className={`${styles.resultBox} ${verifyResult.type === "success" ? styles.success : styles.error}`}>
              <strong>{verifyResult.type === "success" ? "✓ Verified" : "✕ Failed"}:</strong> {verifyResult.message}
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Logs ──────────────────────────────── */}
      {activeTab === "logs" && (
        <div>
          <div className={styles.filtersBar}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Search</label>
              <input
                className={styles.filterInput}
                type="text"
                placeholder="Search by mobile number"
                value={logSearch}
                onChange={(e) => setLogSearch(e.target.value)}
              />
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>Status</label>
              <select className={styles.filterSelect} value={logStatusFilter} onChange={(e) => setLogStatusFilter(e.target.value)}>
                <option value="">All</option>
                <option value="sent">Sent</option>
                <option value="verified">Verified</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <button className={styles.searchBtn} onClick={() => fetchLogs(1)}>Search</button>
          </div>

          {logsLoading ? (
            <div className={styles.spinner}>
              <div className={styles.spinnerDot} />
              <div className={styles.spinnerDot} />
              <div className={styles.spinnerDot} />
            </div>
          ) : logs.length === 0 ? (
            <div className={styles.emptyState}>No OTP requests found</div>
          ) : (
            <>
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Identifier</th>
                      <th>Request ID</th>
                      <th>Provider</th>
                      <th>Purpose</th>
                      <th>Status</th>
                      <th>Sent Time</th>
                      <th>Verified Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td style={{ fontWeight: 500 }}>{log.identifier}</td>
                        <td style={{ fontSize: 12, fontFamily: "monospace", color: "var(--text-muted)" }}>
                          {log.req_id ? log.req_id.substring(0, 16) + "…" : "—"}
                        </td>
                        <td>{log.provider}</td>
                        <td style={{ textTransform: "capitalize" }}>{log.purpose?.replace(/_/g, " ") || "—"}</td>
                        <td>
                          <span className={`${styles.statusBadge} ${styles[log.status] || ""}`}>
                            {log.status}
                          </span>
                        </td>
                        <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{formatDate(log.created_at)}</td>
                        <td style={{ fontSize: 13, color: "var(--text-muted)" }}>{formatDate(log.verified_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {renderPagination()}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SmsNotification;
